import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlusCircle, Copy, Plus, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import type { CarouselApi } from '@/components/ui/carousel';
import type { Indicator } from '@/types/survey';
import { useSurveyIndicators, useCreateSurvey, useSearchSurveys } from '@/hooks/useSurveyQueries';
import { surveyService } from '@/services/api/surveyService';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import useOrgDataStore from '@/stores/org-store';
import { useRBACStore, UserRole } from '@/stores/rbac-store';
import { toast } from "sonner";
import { ConsentTemplateEditor, ConsentTemplate } from '@/features/survey/components/ConsentTemplateEditor';
import type { ConsentType } from '@/types/survey';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { sanitizeConsentTemplateHtml } from '@/utils/htmlSanitizer';
import { surveySentryUtils, trackSurveyUserAction } from '@/features/survey/utils/sentry-integration';
import { withSurveyErrorBoundary } from '@/features/survey/components/SurveyErrorBoundary';
import { useIndicatorConflicts } from '@/hooks/useIndicatorConflicts';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface FormData {
  name: string;
  period: string;
  year: string;
  selectedIndicators: string[];
  selectedAppliesTo: string;
  designatedRecipients: string[];
  selectedAgeGroup: string;
  selectedGrades: string[];
  gradeNames: Record<string, string>;
  consentType: ConsentType;
  consentTemplate: ConsentTemplate;
  selectedTemplate?: string; // Add template selection
  wellnessCenterCheck: boolean;
  selfRegistration: boolean;
  anonymizeParticipants: boolean;
}

interface FormErrors {
  name?: string;
  period?: string;
  year?: string;
  selectedAgeGroup?: string;
  selectedGrades?: string;
  [key: string]: string | undefined;
}

interface SurveyResponse {
  indicators: Indicator[];
  surveys?: { 
    surveyId: string; 
    surveyName: string;
    surveyStatus: string;
    appliedTo?: string;
    parentOrgId?: string;
    orgId?: string;
    year?: number;
    createdAt?: number;
    semester?: string;
    schoolName?: string;
    isArchived?: boolean;
    // These fields are not in the search response but will be populated when fetching full survey details
    indicators?: Indicator[]; 
    indicatorsV2?: Indicator[]; 
    consentType?: ConsentType; 
    consentTemplate?: ConsentTemplate 
  }[];
}

const GRADE_OPTIONS: Record<string, string[]> = {
  'k3': ['K', '01', '02', '03'],
  'elementary': ['03', '04', '05'],
  'uppergrades': ['06', '07', '08', '09', '10', '11', '12']
};

const AGE_GROUP_LABELS: Record<string, string> = {
  'k3': 'Elementary Grade K-3',
  'elementary': 'Grades 3-5 (typical ages 9-11)',
  'uppergrades': 'Grades 6-12 (typical ages 12-17)',
  'adult': 'Adult',
};

const TYPE_COLORS: Record<string, { bg: string; border: string; text: string; hover: string }> = {
  'Cognitive': { 
    bg: 'bg-blue-50', 
    border: 'border-blue-200', 
    text: 'text-blue-700',
    hover: 'hover:bg-blue-100'
  },
  'Social': { 
    bg: 'bg-green-50', 
    border: 'border-green-200', 
    text: 'text-green-700',
    hover: 'hover:bg-green-100'
  },
  'Emotional': { 
    bg: 'bg-purple-50', 
    border: 'border-purple-200', 
    text: 'text-purple-700',
    hover: 'hover:bg-purple-100'
  },
  'Physical': { 
    bg: 'bg-orange-50', 
    border: 'border-orange-200', 
    text: 'text-orange-700',
    hover: 'hover:bg-orange-100'
  },
  'Language': { 
    bg: 'bg-pink-50', 
    border: 'border-pink-200', 
    text: 'text-pink-700',
    hover: 'hover:bg-pink-100'
  },
  'default': { 
    bg: 'bg-gray-50', 
    border: 'border-gray-200', 
    text: 'text-gray-700',
    hover: 'hover:bg-gray-100'
  },
};

export const CreateAssessmentPage = () => {
  const { t } = useTranslation('survey');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { selectedSchool, selectedDistrict } = useOrgDataStore();
  const { getUserRole } = useRBACStore();
  const userRole = getUserRole();
  const isTmAdmin = userRole === UserRole.TM_ADMIN;
  const [api, setApi] = useState<CarouselApi>();
  const [templateChoice, setTemplateChoice] = useState<'new' | 'template' | 'batch'>('new');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [showB2CVariants, setShowB2CVariants] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    period: '',
    year: new Date().getFullYear().toString(),
    selectedIndicators: [],
    selectedAppliesTo: '',
    designatedRecipients: user?.attributes?.email ? [user.attributes.email] : [],
    selectedAgeGroup: '',
    selectedGrades: [],
    gradeNames: {},
    consentType: '',
    consentTemplate: { subject: '', body: '' },
    wellnessCenterCheck: false,
    selfRegistration: false,
    anonymizeParticipants: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showAnonymizeConfirmDialog, setShowAnonymizeConfirmDialog] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const anonymizeWarningMessage = 'Participants personally identifiable information (PII) will be obfuscated. All results are accessible but you will not be able to identify individual students and no historical data will be presented.';

  const { data: response } = useSurveyIndicators(true, false, selectedDistrict?.orgId);
  const createSurveyMutation = useCreateSurvey({
    onSuccess: () => {
      // Remove individual success handling as we'll handle it in batch
    },
    onError: (error) => {
      // Remove individual error handling as we'll handle it in batch
      console.error('Failed to create survey:', error);
    }
  });

  // Fetch existing surveys for templates
  const { data: surveysResponse, isLoading: surveysLoading } = useSearchSurveys({
    districtId: selectedSchool?.parentOrgId,
    schoolId: selectedSchool?.orgId,
    v2: true,
  }, {
    staleTime: 0, // Always fetch fresh data
    gcTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const allIndicators: Indicator[] = ((response as unknown) as SurveyResponse)?.indicators || [];
  const existingSurveys = ((surveysResponse as unknown) as { surveys: SurveyResponse['surveys'] })?.surveys || [];

  // Filter indicators based on role and b2c variant toggle
  const indicators: Indicator[] = allIndicators.filter((indicator: Indicator) => {
    const isB2CVariant = indicator.variant?.toLowerCase() === 'b2c';
    
    // If user is not tm_admin, always filter out b2c variants
    if (!isTmAdmin && isB2CVariant) {
      return false;
    }
    
    // If user is tm_admin, respect the toggle state
    if (isTmAdmin && isB2CVariant && !showB2CVariants) {
      return false;
    }
    
    return true;
  });

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  const periods = ['fall', 'winter', 'spring', 'summer', 'first', 'second', 'third', 'fourth'];

  // Group unfiltered indicators by appliesTo (for stable domain ordering)
  const allAppliesToGroups = allIndicators.reduce((acc: Record<string, Indicator[]>, indicator: Indicator) => {
    indicator.appliesTo.forEach(appliesTo => {
      if (!acc[appliesTo]) {
        acc[appliesTo] = [];
      }
      acc[appliesTo].push(indicator);
    });
    return acc;
  }, {});

  // Group filtered indicators by appliesTo
  const appliesToGroups = indicators.reduce((acc: Record<string, Indicator[]>, indicator: Indicator) => {
    indicator.appliesTo.forEach(appliesTo => {
      if (!acc[appliesTo]) {
        acc[appliesTo] = [];
      }
      acc[appliesTo].push(indicator);
    });
    return acc;
  }, {});

  // Sort indicators within each group by orderIndex
  Object.keys(appliesToGroups).forEach(appliesTo => {
    const group = appliesToGroups[appliesTo];
    if (Array.isArray(group)) {
      group.sort((a: Indicator, b: Indicator) => {
        const aIndex = typeof a.orderIndex === 'string' ? parseInt(a.orderIndex) || 0 : a.orderIndex;
        const bIndex = typeof b.orderIndex === 'string' ? parseInt(b.orderIndex) || 0 : b.orderIndex;
        return aIndex - bIndex;
      });
    }
  });

  // Use the indicator conflicts hook
  const { 
    checkConflict, 
    validateSelection, 
    resolveBulkSelection
  } = useIndicatorConflicts({
    indicators,
    selectedIndicators: formData.selectedIndicators
  });

  // Helper function to check if an indicator has conflict (based on domainId)
  const hasDomainIdConflict = (indicator: Indicator): boolean => {
    const conflict = checkConflict(indicator);
    return conflict.hasConflict;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Assessment name is required';
      surveySentryUtils.trackFormValidationError('CreateAssessment', 'name', 'Assessment name is required');
    }
    if (!formData.period) {
      newErrors.period = 'Period is required';
      surveySentryUtils.trackFormValidationError('CreateAssessment', 'period', 'Period is required');
    }
    if (!formData.year) {
      newErrors.year = 'Year is required';
      surveySentryUtils.trackFormValidationError('CreateAssessment', 'year', 'Year is required');
    }
    if (!formData.selectedAgeGroup) {
      newErrors.selectedAgeGroup = 'Age group is required';
      surveySentryUtils.trackFormValidationError('CreateAssessment', 'selectedAgeGroup', 'Age group is required');
    }
    if (templateChoice === 'batch' && (!formData.selectedGrades || formData.selectedGrades.length === 0)) {
      newErrors.selectedGrades = 'At least one grade must be selected';
      surveySentryUtils.trackFormValidationError('CreateAssessment', 'selectedGrades', 'At least one grade must be selected');
    }
    if (!selectedSchool?.orgId) {
      // You might want to show an error message about selecting an organization
      return false;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    // Track form submission
    trackSurveyUserAction('form submission started', {
      templateChoice,
      selectedAgeGroup: formData.selectedAgeGroup,
      indicatorCount: formData.selectedIndicators.length,
      gradeCount: formData.selectedGrades.length,
    });
    
    if (validateForm() && selectedSchool?.orgId) {
      setIsSubmitting(true);
      
      try {
        if (templateChoice === 'batch') {
          // Track batch creation start
          surveySentryUtils.addBreadcrumb('Batch survey creation started', 'survey-action', 'info', {
            gradeCount: formData.selectedGrades.length,
            selectedAgeGroup: formData.selectedAgeGroup,
            period: formData.period,
            year: formData.year,
          });
          
          // Create an array of promises for each survey creation
          const creationPromises = formData.selectedGrades.map(grade => {
            // If Kindergarten (k3) is selected, use 'elementary' for appliedTo
            const appliedToValue = formData.selectedAgeGroup === 'k3' ? 'elementary' : formData.selectedAgeGroup;
            
            const surveyData = {
              orgId: selectedSchool.orgId!,
              surveyName: formData.gradeNames[grade] || `${formData.selectedAgeGroup.charAt(0).toUpperCase() + formData.selectedAgeGroup.slice(1)} Grade ${grade} ${t(`pages.create.details.form.period.options.${formData.period}`)} ${formData.year}`,
              survey: {
                indicators: formData.selectedIndicators,
                profession: "profession_noprofession",
                quarter: formData.period,
                year: parseInt(formData.year),
                appliedTo: appliedToValue,
                categories: [],
                designatedRecipient: formData.designatedRecipients,
                wellnessCenterCheck: formData.wellnessCenterCheck,
                selfRegistration: formData.selfRegistration,
                isAnonymous: formData.anonymizeParticipants
              },
              participants: [],
              consentType: formData.consentType,
              consentTemplate: formData.consentTemplate
            };
            
            return createSurveyMutation.mutateAsync(surveyData);
          });

          // Wait for all surveys to be created
          await Promise.all(creationPromises);
          
          // Track successful batch creation
          surveySentryUtils.addBreadcrumb('Batch survey creation completed', 'survey-action', 'info', {
            gradeCount: formData.selectedGrades.length,
            successCount: formData.selectedGrades.length,
          });
          
          // Show a single success message
          toast.success(`Successfully created ${formData.selectedGrades.length} assessments`);
          
          // Add a small delay to ensure cache invalidation completes before navigation
          setTimeout(() => {
            // Navigate to assessment list with success state
            navigate('/assessment', {
              state: {
                batchSuccess: {
                  count: formData.selectedGrades.length,
                  period: t(`pages.create.details.form.period.options.${formData.period}`),
                  year: formData.year
                }
              }
            });
          }, 100);
        } else {
          // Track single creation start
          surveySentryUtils.addBreadcrumb('Single survey creation started', 'survey-action', 'info', {
            surveyName: formData.name,
            selectedAgeGroup: formData.selectedAgeGroup,
            period: formData.period,
            year: formData.year,
          });
          
          // Single survey creation
          // If Kindergarten (k3) is selected, use 'elementary' for appliedTo
          const appliedToValue = formData.selectedAgeGroup === 'k3' ? 'elementary' : formData.selectedAgeGroup;
          
          const surveyData = {
            orgId: selectedSchool.orgId!,
            surveyName: formData.name,
            survey: {
              indicators: formData.selectedIndicators,
              profession: "profession_noprofession",
              quarter: formData.period,
              year: parseInt(formData.year),
              appliedTo: appliedToValue,
              categories: [],
              designatedRecipient: formData.designatedRecipients,
              wellnessCenterCheck: formData.wellnessCenterCheck,
              selfRegistration: formData.selfRegistration,
              isAnonymous: formData.anonymizeParticipants
            },
            participants: [],
            consentType: formData.consentType,
            consentTemplate: formData.consentTemplate
          };
          
          await createSurveyMutation.mutateAsync(surveyData);
          
          // Track successful single creation
          surveySentryUtils.addBreadcrumb('Single survey creation completed', 'survey-action', 'info', {
            surveyName: formData.name,
          });
          
          toast.success("Assessment created successfully");
          
          // Add a small delay to ensure cache invalidation completes before navigation
          setTimeout(() => {
            navigate('/assessment');
          }, 100);
        }
      } catch (error) {
        console.log('[CreateAssessmentPage][handleSubmit] Error:', error)
        
        // Track creation error
        surveySentryUtils.trackSurveyAPIError(
          error as Error,
          '/survey',
          undefined,
          {
            templateChoice,
            selectedAgeGroup: formData.selectedAgeGroup,
            indicatorCount: formData.selectedIndicators.length,
            gradeCount: formData.selectedGrades.length,
          }
        );
        
        // Show error message if any survey creation fails
        const errorMessage = templateChoice === 'batch' 
          ? "Failed to create one or more assessments. Please try again."
          : "Failed to create assessment";
        toast.error(errorMessage);
        console.error('Survey creation error:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (name: keyof FormData, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Auto-generate name when age group, period, or year changes
      if ((name === 'selectedAgeGroup' || name === 'period' || name === 'year') && 
          newData.selectedAgeGroup && newData.period && newData.year) {
        // Use the key in camel case (K3, Elementary, Uppergrades, Adult)
        const ageGroupKey = newData.selectedAgeGroup.charAt(0).toUpperCase() + newData.selectedAgeGroup.slice(1);
        const periodLabel = t(`pages.create.details.form.period.options.${newData.period}`);
        newData.name = `${ageGroupKey} ${periodLabel} ${newData.year}`;
      }
      
      return newData;
    });
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAddRecipient = () => {
    setFormData(prev => ({
      ...prev,
      designatedRecipients: [...prev.designatedRecipients, '']
    }));
  };

  const handleRecipientChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      designatedRecipients: prev.designatedRecipients.map((email, i) => 
        i === index ? value : email
      )
    }));
  };

  const handleRemoveRecipient = (index: number) => {
    setFormData(prev => ({
      ...prev,
      designatedRecipients: prev.designatedRecipients.filter((_, i) => i !== index)
    }));
  };

  const handleIndicatorToggle = (indicatorId: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedIndicators.includes(indicatorId);
      const indicator = indicators?.find(ind => ind.indicatorId === indicatorId);
      
      if (!isSelected && !validateSelection(indicatorId)) {
        return prev;
      }
      
      const newSelectedIndicators = isSelected
        ? prev.selectedIndicators.filter(id => id !== indicatorId)
        : [...prev.selectedIndicators, indicatorId];
      
      // Track indicator selection/deselection
      if (indicator) {
        surveySentryUtils.trackIndicatorSelection(
          indicatorId,
          indicator.displayAs || indicator.indicator,
          isSelected ? 'removed' : 'added'
        );
      }
      
      return {
        ...prev,
        selectedIndicators: newSelectedIndicators
      };
    });
  };

  const handleSelectAllType = (typeIndicators: Indicator[]) => {
    // Only consider selectable indicators (not those with conflicts)
    const selectableIndicators = typeIndicators.filter(indicator => {
      const isSelected = formData.selectedIndicators.includes(indicator.indicatorId);
      const hasConflict = hasDomainIdConflict(indicator);
      return isSelected || !hasConflict; // Include if already selected or if no conflict
    });
    
    const selectableIndicatorIds = selectableIndicators.map(indicator => indicator.indicatorId);
    const allSelectableSelected = selectableIndicatorIds.every(id => formData.selectedIndicators.includes(id));
    
    setFormData(prev => {
      if (allSelectableSelected) {
        // Deselecting all selectable indicators in this type
        const indicatorIds = typeIndicators.map(indicator => indicator.indicatorId);
        return {
          ...prev,
          selectedIndicators: prev.selectedIndicators.filter(id => !indicatorIds.includes(id))
        };
      } else {
        // Selecting all - handle domainId conflicts by prioritizing non-B2C and "with Self-Harm" variants
        const indicatorsToAdd = typeIndicators.filter(ind => 
          !prev.selectedIndicators.includes(ind.indicatorId)
        );
        
        const validIndicatorsToAdd = resolveBulkSelection(indicatorsToAdd, (skipped) => {
          toast.info(`Selected all available indicators. Skipped conflicting domains: ${skipped.join(', ')}`);
        });
        
        return {
          ...prev,
          selectedIndicators: [...new Set([...prev.selectedIndicators, ...validIndicatorsToAdd])]
        };
      }
    });
  };

  const handleNextSlide = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    api?.scrollNext();
  };

  const handleGradeToggle = (grade: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedGrades.includes(grade);
      const newSelectedGrades = isSelected
        ? prev.selectedGrades.filter(g => g !== grade)
        : [...prev.selectedGrades, grade];

      // Update grade names when grades are selected/deselected
      const newGradeNames = { ...prev.gradeNames };
      if (!isSelected) {
        newGradeNames[grade] = `Grade ${grade} ${prev.period} ${prev.year}`;
      } else {
        delete newGradeNames[grade];
      }

      return {
        ...prev,
        selectedGrades: newSelectedGrades,
        gradeNames: newGradeNames
      };
    });
  };

  const handleGradeNameChange = (grade: string, name: string) => {
    setFormData(prev => ({
      ...prev,
      gradeNames: {
        ...prev.gradeNames,
        [grade]: name
      }
    }));
  };

  // Function to apply template
  const applyTemplate = async (templateSurveyId: string) => {
    const templateSurvey = existingSurveys.find(survey => survey.surveyId === templateSurveyId);
    if (!templateSurvey) return;

    try {
      // Fetch full survey details to get complete indicator information
      const fullSurveyResponse = await surveyService.getSurveyById(templateSurveyId);
      const fullSurvey = fullSurveyResponse;

      // Use indicatorsV2 from the full survey response if available, otherwise fall back to indicators (string[])
      let templateIndicators: string[] = [];
      if (fullSurvey.indicatorsV2 && fullSurvey.indicatorsV2.length > 0) {
        templateIndicators = fullSurvey.indicatorsV2.map((indicator: { indicatorId: string }) => indicator.indicatorId);
      } else if (fullSurvey.indicators && fullSurvey.indicators.length > 0) {
        // Map indicator names to indicatorIds using the loaded indicators list
        if (Array.isArray(fullSurvey.indicators) && typeof fullSurvey.indicators[0] === 'string') {
          templateIndicators = (fullSurvey.indicators as unknown as string[])
            .map((indicatorName) => {
              const found = indicators.find((ind: Indicator) => ind.indicator === indicatorName);
              return found ? found.indicatorId : null;
            })
            .filter((id): id is string => Boolean(id));
        } else {
          console.warn('Template indicators are not string array:', fullSurvey.indicators);
        }
      }

      setFormData(prev => ({
        ...prev,
        selectedIndicators: templateIndicators,
        selectedAppliesTo: fullSurvey.appliedTo || '',
        selectedAgeGroup: fullSurvey.appliedTo || '',
        consentType: (fullSurvey.consentType as ConsentType) || '',
        consentTemplate: fullSurvey.consentTemplate ? { 
          subject: fullSurvey.consentTemplate.subject || '', 
          body: fullSurvey.consentTemplate.body || '' 
        } : { subject: '', body: '' },
        selectedTemplate: templateSurveyId,
      }));

      setSelectedTemplate(templateSurveyId);
      toast.success('Template applied successfully');
    } catch (error) {
      console.error('Error fetching full survey details:', error);
      toast.error('Failed to load template details. Please try again.');
    }
  };

  // Function to clear template
  const clearTemplate = () => {
    setFormData(prev => ({
      ...prev,
      selectedIndicators: [],
      selectedAppliesTo: '',
      selectedAgeGroup: '',
      consentType: '',
      consentTemplate: { subject: '', body: '' },
      selectedTemplate: '',
    }));
    setSelectedTemplate('');
  };

  return (
    <TooltipProvider>
      <div className="container mx-auto py-6">
        <h1 className="text-2xl font-bold mb-6">{t('pages.create.title')}</h1>
        
        <Carousel
          setApi={setApi}
          opts={{
            align: 'start',
            loop: false,
          }}
          className="w-full max-w-2xl mx-auto"
          disableKeyboardNavigation={true}
        >
        <CarouselContent>
          {/* Slide 1: Creation Method */}
          <CarouselItem>
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">{t('pages.create.method.title')}</CardTitle>
                <CardDescription className="text-base">{t('pages.create.method.description')}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    variant={templateChoice === 'new' ? 'default' : 'outline'}
                    onClick={() => setTemplateChoice('new')}
                    className={`h-32 flex flex-col items-center justify-center gap-2 p-4 transition-colors ${
                      templateChoice === 'new' 
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                        : 'hover:bg-primary/10 hover:text-primary'
                    }`}
                  >
                    <PlusCircle className={`h-8 w-8 ${templateChoice === 'new' ? 'text-primary-foreground' : 'text-primary'}`} />
                    <span className={`text-lg font-medium ${templateChoice === 'new' ? 'text-primary-foreground' : 'text-foreground'}`}>
                      {t('pages.create.method.new.title')}
                    </span>
                    <span className={`text-sm ${templateChoice === 'new' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                      {t('pages.create.method.new.description')}
                    </span>
                  </Button>
                  
                        <Button
                    variant={templateChoice === 'template' ? 'default' : 'outline'}
                    onClick={() => setTemplateChoice('template')}
                    className={`h-32 flex flex-col items-center justify-center gap-2 p-4 transition-colors ${
                      templateChoice === 'template' 
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                        : 'hover:bg-primary/10 hover:text-primary'
                    }`}
                  >
                    <Copy className={`h-8 w-8 ${templateChoice === 'template' ? 'text-primary-foreground' : 'text-primary'}`} />
                    <span className={`text-lg font-medium ${templateChoice === 'template' ? 'text-primary-foreground' : 'text-foreground'}`}>
                            {t('pages.create.method.template.title')}
                          </span>
                    <span className={`text-sm ${templateChoice === 'template' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                            {t('pages.create.method.template.description')}
                          </span>
                        </Button>

                  <Button
                    variant={templateChoice === 'batch' ? 'default' : 'outline'}
                    onClick={() => setTemplateChoice('batch')}
                    className={`h-32 flex flex-col items-center justify-center gap-2 p-4 transition-colors ${
                      templateChoice === 'batch' 
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                        : 'hover:bg-primary/10 hover:text-primary'
                    }`}
                  >
                    <Copy className={`h-8 w-8 ${templateChoice === 'batch' ? 'text-primary-foreground' : 'text-primary'}`} />
                    <span className={`text-lg font-medium ${templateChoice === 'batch' ? 'text-primary-foreground' : 'text-foreground'}`}>
                      {t('pages.create.method.batch.title')}
                    </span>
                    <span className={`text-sm ${templateChoice === 'batch' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                      {t('pages.create.method.batch.description')}
                    </span>
                  </Button>
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    onClick={handleNextSlide}
                    className="px-8"
                    disabled={isSubmitting}
                  >
                    {t('common:continue')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </CarouselItem>

          {/* Slide 2: Assessment Details */}
          <CarouselItem>
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">{t('pages.create.details.title')}</CardTitle>
                <CardDescription className="text-base">{t('pages.create.details.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Age Group</label>
                    <Select
                      value={formData.selectedAgeGroup}
                      onValueChange={(value) => {
                        setFormData(prev => ({
                          ...prev,
                          selectedAgeGroup: value,
                          selectedIndicators: [], // Clear indicators when changing age group
                          selectedGrades: [], // Clear selected grades when changing age group
                          gradeNames: {}, // Clear grade names when changing age group
                          wellnessCenterCheck: value === 'adult', // Set wellnessCenterCheck based on age group
                        }));

                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select age group" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(appliesToGroups)
                          .filter(ageGroup => templateChoice !== 'batch' || ageGroup !== 'adult')
                          .sort((a, b) => {
                            const order = ['k3', 'elementary', 'uppergrades', 'adult'];
                            return order.indexOf(a) - order.indexOf(b);
                          })
                          .map((ageGroup) => (
                            <SelectItem key={ageGroup} value={ageGroup}>
                              {AGE_GROUP_LABELS[ageGroup] || ageGroup}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {errors.selectedAgeGroup && <p className="text-sm text-red-500">{errors.selectedAgeGroup}</p>}
                  </div>


                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('pages.create.details.form.period.label')}</label>
                    <Select
                      value={formData.period}
                      onValueChange={(value) => handleInputChange('period', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('pages.create.details.form.period.placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {periods.map((period) => (
                          <SelectItem key={period} value={period}>
                            {t(`pages.create.details.form.period.options.${period}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.period && <p className="text-sm text-red-500">{errors.period}</p>}
                  </div>


                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('pages.create.details.form.year.label')}</label>
                    <Select
                      value={formData.year}
                      onValueChange={(value) => handleInputChange('year', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('pages.create.details.form.year.placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((year) => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.year && <p className="text-sm text-red-500">{errors.year}</p>}
                  </div>


                  {templateChoice !== 'batch' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">{t('pages.create.details.form.name.label')}</label>
                      <div className="relative">
                        <Input
                          placeholder={t('pages.create.details.form.name.placeholder')}
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className={formData.name ? "pr-24" : ""}
                        />
                        {formData.name && (
                          <div className="absolute right-0 top-0 h-full flex items-center pr-3 text-sm text-muted-foreground">
                            {t('pages.create.details.form.name.suggestion')}
                          </div>
                        )}
                      </div>
                      {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
                    </div>
                  )}

                  {templateChoice === 'batch' && formData.selectedAgeGroup && GRADE_OPTIONS[formData.selectedAgeGroup] && (
                    <div className="space-y-4">
                      <label className="text-sm font-medium">Select Grades</label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-4">
                          {GRADE_OPTIONS[formData.selectedAgeGroup].map((grade) => (
                            <div key={grade} className="flex items-center space-x-2">
                              <Checkbox
                                id={`grade-${grade}`}
                                checked={formData.selectedGrades.includes(grade)}
                                onCheckedChange={() => handleGradeToggle(grade)}
                              />
                              <label htmlFor={`grade-${grade}`} className="text-sm font-medium">
                                Grade {grade}
                              </label>
                            </div>
                          ))}
                        </div>
                        <div className="space-y-4">
                          {formData.selectedGrades.map((grade) => (
                            <div key={grade} className="flex items-center gap-2">
                              <Input
                                value={formData.gradeNames[grade] || ''}
                                onChange={(e) => handleGradeNameChange(grade, e.target.value)}
                                placeholder={`Grade ${grade} ${formData.period} ${formData.year}`}
                                className="text-sm"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                      {errors.selectedGrades && <p className="text-sm text-red-500">{errors.selectedGrades}</p>}
                    </div>
                  )}

                  {!formData.anonymizeParticipants && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Designated Recipients</label>
                      <div className="space-y-2">
                        {formData.designatedRecipients.map((email, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              type="email"
                              placeholder="Enter email address"
                              value={email}
                              onChange={(e) => handleRecipientChange(index, e.target.value)}
                              readOnly={index === 0}
                              className={index === 0 ? "bg-muted" : ""}
                            />
                            {index > 0 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => handleRemoveRecipient(index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={handleAddRecipient}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Recipient
                        </Button>
                      </div>
                    </div>
                  )}


                  {/* Wellness Center Check - Only show for adult age group */}
                  {formData.selectedAgeGroup === 'adult' && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="wellness-center-check"
                          checked={formData.wellnessCenterCheck}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              wellnessCenterCheck: checked as boolean
                            }));
                          }}
                        />
                        <div className="flex items-center space-x-2">
                          <label htmlFor="wellness-center-check" className="text-sm font-medium">
                            {t('pages.create.details.form.wellnessCenterCheck.label')}
                          </label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help">
                                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t('pages.create.details.form.wellnessCenterCheck.tooltip')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Self Registration Check - Only show for tm_admin */}
                  {isTmAdmin && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="self-registration-check"
                          checked={formData.selfRegistration}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              selfRegistration: checked as boolean
                            }));
                          }}
                        />
                        <div className="flex items-center gap-2">
                          <label htmlFor="self-registration-check" className="text-sm font-medium">
                            Allow self registration with link/QR code
                          </label>
                          <Badge variant="outline" className="text-xs px-2 py-0.5 border-blue-300 text-blue-600 bg-blue-50">
                            Admin Only
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="anonymize-participants-check"
                        checked={formData.anonymizeParticipants}
                        onCheckedChange={(checked) => {
                          if (checked === true) {
                            setShowAnonymizeConfirmDialog(true);
                            return;
                          }

                          setFormData(prev => ({
                            ...prev,
                            anonymizeParticipants: false
                          }));
                        }}
                      />
                      <label htmlFor="anonymize-participants-check" className="text-sm font-medium">
                        Anonymize participants
                      </label>
                    </div>
                  </div>

                  <Dialog open={showAnonymizeConfirmDialog} onOpenChange={setShowAnonymizeConfirmDialog}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Anonymize participants : Are you sure?</DialogTitle>
                        <DialogDescription className="text-sm text-foreground">
                          <p>{anonymizeWarningMessage}</p>
                          <p className="mt-3 font-medium">This action is not reversible once activated.</p>
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex justify-end gap-2 mt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowAnonymizeConfirmDialog(false);
                            setFormData(prev => ({
                              ...prev,
                              anonymizeParticipants: false
                            }));
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            setShowAnonymizeConfirmDialog(false);
                            setFormData(prev => ({
                              ...prev,
                              anonymizeParticipants: true,
                              designatedRecipients: [],
                              consentType: '',
                              consentTemplate: { subject: '', body: '' }
                            }));
                          }}
                        >
                          I understand
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <div className="flex justify-between mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => api?.scrollPrev()}
                      disabled={isSubmitting}
                    >
                      {t('common:back')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        if (validateForm()) {
                          handleNextSlide();
                        }
                      }}
                      disabled={isSubmitting}
                    >
                      {t('common:continue')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </CarouselItem>

          {/* Slide 3: Template Selection (only for template mode) */}
          {templateChoice === 'template' && (
            <CarouselItem>
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Select Template</CardTitle>
                  <CardDescription className="text-base">
                    Choose an existing assessment to use as a template. This will automatically populate the indicators and consent settings.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {surveysLoading ? (
                      <div className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading available templates...</p>
                      </div>
                    ) : existingSurveys.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground">No existing assessments found to use as templates.</p>
                        <Button
                          variant="outline"
                          onClick={() => setTemplateChoice('new')}
                          className="mt-4"
                        >
                          Create New Assessment Instead
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm text-muted-foreground mb-4">
                          Showing {existingSurveys.length} total surveys. Only completed surveys can be used as templates.
                        </div>
                        {(() => {
                          const completedSurveys = existingSurveys.filter(survey => survey.surveyStatus?.toLowerCase() === 'completed');
                          const inProgressSurveys = existingSurveys.filter(survey => survey.surveyStatus?.toLowerCase() === 'in progress');
                          const createdSurveys = existingSurveys.filter(survey => survey.surveyStatus?.toLowerCase() === 'created');
                          

                          
                          if (completedSurveys.length === 0) {
                            return (
                              <div className="text-center py-8">
                                <p className="text-muted-foreground mb-4">
                                  No completed assessments found. You need at least one completed assessment to use as a template.
                                </p>
                                <div className="space-y-2 text-sm text-muted-foreground">
                                  <p>• {inProgressSurveys.length} surveys in progress</p>
                                  <p>• {createdSurveys.length} surveys created</p>
                                </div>
                                <Button
                                  variant="outline"
                                  onClick={() => setTemplateChoice('new')}
                                  className="mt-4"
                                >
                                  Create New Assessment Instead
                                </Button>
                              </div>
                            );
                          }
                          
                          return (
                            <>
                              {selectedTemplate && (
                                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                    <span className="text-sm font-medium text-green-800">
                                      Template selected: {existingSurveys.find(s => s.surveyId === selectedTemplate)?.surveyName}
                                    </span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={clearTemplate}
                                  >
                                    Clear Template
                                  </Button>
                                </div>
                              )}
                              
                              <div className="space-y-3">
                                <h3 className="text-lg font-semibold">Available Templates ({completedSurveys.length})</h3>
                                <div className="grid gap-3">
                                  {completedSurveys.map((survey) => (
                                    <div
                                      key={survey.surveyId}
                                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                                        selectedTemplate === survey.surveyId
                                          ? 'border-primary bg-primary/5'
                                          : 'border-gray-200 hover:border-primary/50 hover:bg-primary/5'
                                      }`}
                                      onClick={() => applyTemplate(survey.surveyId)}
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <h4 className="font-medium text-gray-900">{survey.surveyName}</h4>
                                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                            <span>Age Group: {survey.appliedTo}</span>
                                            <span>Year: {survey.year}</span>
                                            <span>Semester: {survey.semester}</span>
                                            <span>School: {survey.schoolName}</span>
                                          </div>
                                        </div>
                                        {selectedTemplate === survey.surveyId && (
                                          <CheckCircle className="h-5 w-5 text-primary" />
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>

                  <div className="flex justify-between mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => api?.scrollPrev()}
                      disabled={isSubmitting}
                    >
                      {t('common:back')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        if (selectedTemplate) {
                          handleNextSlide();
                        } else {
                          toast.error('Please select a template to continue');
                        }
                      }}
                      disabled={isSubmitting || !selectedTemplate}
                    >
                      {t('common:continue')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </CarouselItem>
          )}

          {/* Slide 3: Indicator Selection (skip if using template) */}
          {templateChoice !== 'template' && (
          <CarouselItem>
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">{t('pages.create.indicators.title')}</CardTitle>
                <CardDescription className="text-base">
                  {formData.selectedAgeGroup 
                    ? `Choose the indicators you want to include for age group: ${AGE_GROUP_LABELS[formData.selectedAgeGroup] || formData.selectedAgeGroup}`
                    : t('pages.create.indicators.description')
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {!formData.selectedAgeGroup ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">Please select an age group first</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                          {t('pages.create.indicators.selected', { count: formData.selectedIndicators.length })}
                        </div>
                        <div className="flex items-center gap-2">
                          {isTmAdmin && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-2">
                                    <Checkbox
                                      id="show-b2c-variants"
                                      checked={showB2CVariants}
                                      onCheckedChange={(checked) => setShowB2CVariants(checked === true)}
                                    />
                                    <label
                                      htmlFor="show-b2c-variants"
                                      className="text-sm font-medium cursor-pointer"
                                    >
                                      Show B2C Variants
                                    </label>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Admin only - using B2C items will make historical comparison impossible</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                selectedIndicators: []
                              }));
                            }}
                          >
                            {t('pages.create.indicators.clearSelection')}
                          </Button>
                        </div>
                      </div>

                      <div className="flex flex-col gap-6">
                        <div className="space-y-8">
                          {Object.entries(
                              (appliesToGroups[formData.selectedAgeGroup] || []).reduce((acc: Record<string, Indicator[]>, indicator: Indicator) => {
                                if (!acc[indicator.type]) {
                                  acc[indicator.type] = [];
                                }
                                acc[indicator.type].push(indicator);
                                return acc;
                              }, {})
                            ).map(([type, typeIndicators]) => {
                              const typeColor = TYPE_COLORS[type] || TYPE_COLORS.default;
                              const selectedCount = formData.selectedIndicators.filter(id => 
                                typeIndicators.find((i: Indicator) => i.indicatorId === id)
                              ).length;

                              return (
                                <div key={type} className="space-y-4">
                                  <Collapsible defaultOpen>
                                    <div className={`flex items-center gap-3 p-4 rounded-lg ${typeColor.bg} ${typeColor.border} border-2 shadow-md w-full hover:shadow-lg transition-all`}>
                                      <CollapsibleTrigger className="flex-1 flex items-center gap-3 cursor-pointer">
                                        <div className={`w-4 h-4 rounded-full ${typeColor.text}`} />
                                        <h4 className={`text-lg font-bold ${typeColor.text}`}>
                                          {type === 'risk' ? 'Adversity' : type === 'resilience' ? 'Resilience' : type}
                                        </h4>
                                        <div className="ml-auto flex items-center gap-2 border-l pl-3">
                                          <span className={`text-sm font-medium ${typeColor.text}`}>
                                            {selectedCount}
                                          </span>
                                          <span className={`text-sm ${typeColor.text}/70`}>/</span>
                                          <span className={`text-sm ${typeColor.text}/70`}>
                                            {typeIndicators.length}
                                          </span>
                                        </div>
                                        <ChevronDown className={`h-5 w-5 ${typeColor.text} transition-transform duration-200 ui-expanded:rotate-180`} />
                                      </CollapsibleTrigger>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSelectAllType(typeIndicators);
                                        }}
                                        className={`text-sm font-medium hover:bg-transparent ${typeColor.text} hover:${typeColor.text}/80`}
                                      >
                                        {(() => {
                                          const selectableIndicators = typeIndicators.filter(indicator => {
                                            const isSelected = formData.selectedIndicators.includes(indicator.indicatorId);
                                            const hasConflict = hasDomainIdConflict(indicator);
                                            return isSelected || !hasConflict;
                                          });
                                          const allSelectableSelected = selectableIndicators.every(indicator => 
                                            formData.selectedIndicators.includes(indicator.indicatorId)
                                          );
                                          return allSelectableSelected ? 'Deselect All' : 'Select All';
                                        })()}
                                      </Button>
                                    </div>
                                    <CollapsibleContent>
                                      <div className="space-y-3 pl-4 border-l-2 border-dashed border-muted pt-3">
                                        {(() => {
                                          // Get all indicators of this type (unfiltered) to establish stable domain order
                                          const allTypeIndicators = (allAppliesToGroups[formData.selectedAgeGroup] || [])
                                            .filter((ind: Indicator) => ind.type === type);
                                          
                                          // Establish stable domain order based on first occurrence in unfiltered indicators
                                          // Use orderIndex to maintain consistent ordering
                                          const domainOrderMap = new Map<string, number>();
                                          allTypeIndicators.forEach((ind: Indicator) => {
                                            const domainId = ind.domainId || 'unknown';
                                            if (!domainOrderMap.has(domainId)) {
                                              const orderIndex = typeof ind.orderIndex === 'string' ? parseInt(ind.orderIndex) || 0 : ind.orderIndex;
                                              domainOrderMap.set(domainId, orderIndex);
                                            }
                                          });
                                          
                                          // Sort domains by their first indicator's orderIndex
                                          const domainOrder = Array.from(domainOrderMap.entries())
                                            .sort((a, b) => a[1] - b[1])
                                            .map(([domainId]) => domainId);
                                          
                                          // Group filtered indicators by domainId
                                          const domainGroups = typeIndicators.reduce((acc: Record<string, Indicator[]>, indicator: Indicator) => {
                                            const domainId = indicator.domainId || 'unknown';
                                            if (!acc[domainId]) {
                                              acc[domainId] = [];
                                            }
                                            acc[domainId].push(indicator);
                                            return acc;
                                          }, {});
                                          
                                          // Return domains in stable order, only showing domains that have filtered indicators
                                          return domainOrder
                                            .filter(domainId => domainGroups[domainId] && domainGroups[domainId].length > 0)
                                            .map((domainId) => [domainId, domainGroups[domainId]] as [string, Indicator[]]);
                                        })().map(([domainId, domainIndicators]) => {
                                            // Sort indicators within domain by orderIndex
                                            const sortedIndicators = domainIndicators.sort((a: Indicator, b: Indicator) => {
                                              const aIndex = typeof a.orderIndex === 'string' ? parseInt(a.orderIndex) || 0 : a.orderIndex;
                                              const bIndex = typeof b.orderIndex === 'string' ? parseInt(b.orderIndex) || 0 : b.orderIndex;
                                              return aIndex - bIndex;
                                            });

                                            // Determine if the box should be highlighted (if any indicator is selected)
                                            const hasAnySelected = sortedIndicators.some(ind => 
                                              formData.selectedIndicators.includes(ind.indicatorId)
                                            );
                                            const hasAnyConflict = sortedIndicators.some(ind => 
                                              !formData.selectedIndicators.includes(ind.indicatorId) && hasDomainIdConflict(ind)
                                            );

                                            return (
                                              <div
                                                key={domainId}
                                                className={`p-4 rounded-lg border transition-all ${
                                                  hasAnySelected 
                                                    ? `${typeColor.border} ${typeColor.bg} border-2 shadow-sm ring-2 ring-offset-2` 
                                                    : hasAnyConflict
                                                    ? 'bg-gray-50 border-gray-200'
                                                    : `bg-white border-gray-200 hover:border-2 ${typeColor.hover}`
                                                }`}
                                              >
                                                <div className="space-y-3">
                                                  {sortedIndicators.map((indicator: Indicator) => {
                                                    const isSelected = formData.selectedIndicators.includes(indicator.indicatorId);
                                                    const hasConflict = hasDomainIdConflict(indicator);
                                                    const isDisabled = !isSelected && hasConflict;
                                                    
                                                    return (
                                                      <div
                                                        key={indicator.indicatorId}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => !isDisabled && handleIndicatorToggle(indicator.indicatorId)}
                                                        onKeyDown={(e) => {
                                                          if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
                                                            e.preventDefault();
                                                            handleIndicatorToggle(indicator.indicatorId);
                                                          }
                                                        }}
                                                        className={`flex items-start gap-4 ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                                      >
                                                        <div 
                                                          className="mt-1 flex-shrink-0"
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          <Checkbox
                                                            id={indicator.indicatorId}
                                                            checked={isSelected}
                                                            disabled={isDisabled}
                                                            onCheckedChange={() => !isDisabled && handleIndicatorToggle(indicator.indicatorId)}
                                                            className={`${isSelected ? typeColor.text : 'text-gray-400'}`}
                                                          />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                            <label
                                                              htmlFor={indicator.indicatorId}
                                                              className={`text-base font-medium leading-tight ${isSelected ? typeColor.text : 'text-gray-900'}`}
                                                            >
                                                              {indicator.displayAs || indicator.indicator}
                                                            </label>
                                                            {indicator.variant && indicator.variant !== 'default' && (
                                                              <div className={`px-2.5 py-1 rounded-full text-sm whitespace-nowrap
                                                                ${isSelected 
                                                                  ? `${typeColor.bg} ${typeColor.text}` 
                                                                  : 'bg-gray-100 text-gray-600'
                                                                }`}
                                                              >
                                                                {indicator.variant}
                                                              </div>
                                                            )}
                                                            {hasConflict && (
                                                              <TooltipProvider>
                                                                <Tooltip>
                                                                  <TooltipTrigger asChild>
                                                                    <Badge 
                                                                      variant="outline" 
                                                                      className="text-xs px-2 py-0.5 border-red-300 text-red-600 bg-red-50"
                                                                    >
                                                                      Conflict
                                                                    </Badge>
                                                                  </TooltipTrigger>
                                                                  <TooltipContent>
                                                                    <p>Cannot select - an indicator with domain "{indicator.domainId || 'unknown'}" is already selected</p>
                                                                  </TooltipContent>
                                                                </Tooltip>
                                                              </TooltipProvider>
                                                            )}
                                                          </div>
                                                          <p className={`text-sm ${isSelected ? 'text-gray-700' : 'text-gray-500'}`}>
                                                            {indicator.interpretation}
                                                          </p>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          })}
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => api?.scrollPrev()}
                      disabled={isSubmitting}
                    >
                      {t('common:back')}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleNextSlide}
                      disabled={formData.selectedIndicators.length === 0 || !formData.selectedAgeGroup || isSubmitting}
                    >
                      {t('common:continue')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CarouselItem>
          )}

          {/* Slide 4: Consent Management (skip if using template, self registration, or anonymized survey) */}
          {templateChoice !== 'template' && !formData.selfRegistration && !formData.anonymizeParticipants && (
          <CarouselItem>
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Consent Management</CardTitle>
                <CardDescription className="text-base">Configure how parent consent is handled for this survey</CardDescription>
              </CardHeader>
              <CardContent>
                <ConsentTemplateEditor
                  consentType={formData.consentType}
                  consentTemplate={formData.consentTemplate}
                  onConsentTypeChange={(type) => {
                    setFormData(prev => ({
                      ...prev,
                      consentType: type
                    }));
                  }}
                  onConsentTemplateChange={(template) => {
                    setFormData(prev => ({
                      ...prev,
                      consentTemplate: template
                    }));
                  }}
                />

                <div className="flex justify-between mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => api?.scrollPrev()}
                    disabled={isSubmitting}
                  >
                    {t('common:back')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleNextSlide}
                    disabled={isSubmitting}
                  >
                    {t('common:continue')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </CarouselItem>
          )}

          {/* Slide 5: Review */}
          <CarouselItem>
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">{t('pages.create.review.title')}</CardTitle>
                <CardDescription className="text-base">{t('pages.create.review.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Assessment Period & Year */}
                    <div className="p-6 rounded-lg bg-muted/30 space-y-6">
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-primary">{t('pages.create.details.form.period.label')}</h3>
                        <p className="text-lg font-medium">{t(`pages.create.details.form.period.options.${formData.period}`)}</p>
                      </div>

                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-primary">{t('pages.create.details.form.year.label')}</h3>
                        <p className="text-lg font-medium">{formData.year}</p>
                      </div>
                    </div>

                    {/* Recipients */}
                    <div className="p-6 rounded-lg bg-muted/30 space-y-3">
                      <h3 className="text-lg font-semibold text-primary">Designated Recipients</h3>
                      <div className="space-y-2">
                        {formData.designatedRecipients.map((email, index) => (
                          <div key={index} className="p-2 rounded bg-background border flex items-center">
                            <span className="text-sm font-medium">{email}</span>
                            {index === 0 && <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Primary</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Assessment Details */}
                  <div className="rounded-lg border-2 border-primary/10 p-6 space-y-4">
                    {templateChoice === 'batch' ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between border-b pb-4">
                          <h3 className="text-xl font-semibold text-primary">{t('pages.create.review.batch.title')}</h3>
                          <div className="px-4 py-2 bg-primary/10 rounded-full">
                            <span className="text-sm font-semibold text-primary">
                              {t('pages.create.review.batch.count_assessments', { 
                                count: formData.selectedGrades.length
                              })}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center gap-2">
                            <span className="text-base font-medium">{AGE_GROUP_LABELS[formData.selectedAgeGroup]}</span>
                            <span className="px-3 py-1 rounded-full bg-muted text-sm">
                              {t('pages.create.review.batch.count_grades', { 
                                count: formData.selectedGrades.length
                              })}
                            </span>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            {formData.selectedGrades.map((grade) => (
                              <div key={grade} className="p-4 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
                                <div className="font-semibold text-primary">{t('pages.create.review.batch.grade_number', { grade })}</div>
                                <div className="text-sm mt-1">
                                  {formData.gradeNames[grade] || `Grade ${grade} ${formData.period} ${formData.year}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <h3 className="text-xl font-semibold text-primary">{t('pages.create.details.form.name.label')}</h3>
                        <p className="text-lg font-medium">{formData.name}</p>
                      </div>
                    )}
                  </div>

                  {/* Template Information */}
                  {templateChoice === 'template' && selectedTemplate && (
                    <div className="rounded-lg border-2 border-primary/10 p-6">
                      <h3 className="text-xl font-semibold text-primary mb-6">Template Information</h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <span className="text-base font-medium">Template:</span>
                          <span className="px-3 py-1 rounded-full bg-muted text-sm">
                            {existingSurveys.find(s => s.surveyId === selectedTemplate)?.surveyName}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-base font-medium">Age Group:</span>
                          <span className="px-3 py-1 rounded-full bg-muted text-sm">
                            {formData.selectedAgeGroup}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-base font-medium">Indicators:</span>
                          <span className="px-3 py-1 rounded-full bg-muted text-sm">
                            {formData.selectedIndicators.length} indicators
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Consent Management (skip if self registration or anonymized survey is enabled) */}
                  {!formData.selfRegistration && !formData.anonymizeParticipants && (
                    <div className="rounded-lg border-2 border-primary/10 p-6">
                      <h3 className="text-xl font-semibold text-primary mb-6">Consent Management</h3>
                      {templateChoice === 'template' && (
                        <div className="mb-2 text-xs text-muted-foreground italic">Consent settings copied from template</div>
                      )}
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <span className="text-base font-medium">Consent Type:</span>
                          <span className="px-3 py-1 rounded-full bg-muted text-sm">
                            {formData.consentType === 'CONSENT_TYPE_ACTIVE_CONSENT' ? 'Active Consent' :
                             formData.consentType === 'CONSENT_TYPE_PASSIVE_CONSENT' ? 'Passive Consent' :
                             'No consent required or not set.'}
                          </span>
                        </div>
                      
                      {/* Consent Template Preview */}
                      {formData.consentType && (formData.consentTemplate.subject || formData.consentTemplate.body) && (
                        <div className="mt-6">
                          <h4 className="text-lg font-semibold text-primary mb-4">Consent Template Preview</h4>
                          <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
                            {/* Email Header */}
                            <div className="bg-gray-50 px-4 py-3 border-b">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">Email Preview</div>
                                  <div className="text-xs text-gray-500">This is how the consent email will appear to parents/guardians</div>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {formData.consentType === 'CONSENT_TYPE_ACTIVE_CONSENT' ? 'Active Consent' : 'Passive Consent'}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* Email Content */}
                            <div className="p-6">
                              {formData.consentTemplate.subject && (
                                <div className="mb-4">
                                  <div className="text-sm font-medium text-gray-700 mb-1">Subject:</div>
                                  <div className="text-base font-semibold text-gray-900">{formData.consentTemplate.subject}</div>
                                </div>
                              )}
                              
                              {formData.consentTemplate.body && (
                                <div>
                                  <div className="text-sm font-medium text-gray-700 mb-2">Email Body:</div>
                                  <div 
                                    className="prose prose-sm max-w-none text-gray-800"
                                    dangerouslySetInnerHTML={{ __html: sanitizeConsentTemplateHtml(formData.consentTemplate.body) }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!formData.consentType && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-600">
                            No consent template configured. Parents/guardians will not receive consent emails.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  )}
                  
                  {/* Self Registration Notice */}
                  {formData.selfRegistration && (
                    <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-6">
                      <h3 className="text-xl font-semibold text-blue-900 mb-2">Self Registration Enabled</h3>
                      <p className="text-sm text-blue-700">
                        This assessment allows self registration via link/QR code. Consent management is not applicable for self-registration surveys.
                      </p>
                    </div>
                  )}

                  {/* Anonymization Notice */}
                  {formData.anonymizeParticipants && (
                    <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-6">
                      <h3 className="text-xl font-semibold text-amber-900 mb-2">Anonymized Participants</h3>
                      <p className="text-sm text-amber-800">
                        Anonymization of participants is enabled
                      </p>
                    </div>
                  )}

                  {/* Selected Indicators */}
                  <div className="rounded-lg border-2 border-primary/10 p-6">
                    <h3 className="text-xl font-semibold text-primary mb-6">Selected Indicators</h3>
                    <div className="space-y-6">
                      {(() => {
                        const selectedIndicators = indicators.filter(indicator => 
                          formData.selectedIndicators.includes(indicator.indicatorId)
                        );

                        if (selectedIndicators.length === 0) {
                          return (
                            <div className="text-center py-8">
                              <p className="text-muted-foreground">No indicators selected</p>
                            </div>
                          );
                        }

                        const indicatorsByType = selectedIndicators.reduce((acc: Record<string, Indicator[]>, indicator: Indicator) => {
                          if (!acc[indicator.type]) {
                            acc[indicator.type] = [];
                          }
                          acc[indicator.type].push(indicator);
                          return acc;
                        }, {});

                        return (
                          <div className="space-y-4">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted">
                              <span className="font-medium">{AGE_GROUP_LABELS[formData.selectedAgeGroup] || formData.selectedAgeGroup}</span>
                              <span className="text-sm text-muted-foreground">
                                ({selectedIndicators.length} indicators)
                              </span>
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                              {Object.entries(indicatorsByType).map(([type, typeIndicators]) => {
                                const typeColor = TYPE_COLORS[type] || TYPE_COLORS.default;
                                return (
                                  <div key={type} className={`p-4 rounded-lg ${typeColor.bg} ${typeColor.border} border-2`}>
                                    <h5 className={`text-base font-semibold ${typeColor.text} mb-3`}>
                                      {type}
                                      <span className="ml-2 text-sm font-normal">
                                        ({typeIndicators.length})
                                      </span>
                                    </h5>
                                    <ul className="space-y-2">
                                      {typeIndicators.map(indicator => (
                                        <li key={indicator.indicatorId} className="text-sm">
                                          {indicator.displayAs || indicator.indicator}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => api?.scrollPrev()}
                      disabled={isSubmitting}
                    >
                      {t('common:back')}
                    </Button>
                    <Button 
                      type="submit"
                      onClick={handleSubmit}
                      className="px-8"
                      size="lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {templateChoice === 'batch' 
                            ? `Creating ${formData.selectedGrades.length} Assessments...` 
                            : 'Creating Assessment...'}
                        </>
                      ) : (
                        templateChoice === 'batch' 
                          ? `Create ${formData.selectedGrades.length} Assessments` 
                          : t('pages.create.submit')
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CarouselItem>
        </CarouselContent>
      </Carousel>
    </div>
    </TooltipProvider>
  );
};

// Export the component wrapped with error boundary
const CreateAssessmentPageWithErrorBoundary = withSurveyErrorBoundary(CreateAssessmentPage);
export default CreateAssessmentPageWithErrorBoundary; 